import { REGEX } from '@/constants/regex';
import { InputRules } from '@/types/inputs';
import { Box, InputAdornment, Button } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { HexColorPicker } from 'react-colorful';
import SVTextInput from './SVTextInput';
import { Edit } from '@mui/icons-material';

type ColorPickerProps = {
  label: string;
  name: string;
  rules?: InputRules;
};

const isNode = (target: EventTarget | null): target is Node => {
  return target instanceof Node;
};

export default function SVColorPicker({
  label,
  name,
  rules,
}: ColorPickerProps) {
  const methods = useFormContext();
  const { watch, setValue, getFieldState } = methods;

  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const color = watch(name) || '#ffffff';

  const colorPickerRules: InputRules = {
    ...rules,
    pattern: {
      regex: REGEX.HEX_COLOR,
      readablePatternName: 'color',
    },
  };

  const handleOnChange = (value: string) => {
    setValue(name, value);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = (event: MouseEvent) => {
    if (
      wrapperRef.current &&
      isNode(event.target) &&
      !wrapperRef.current.contains(event.target)
    ) {
      setColorPickerOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (color[0] !== '#' && getFieldState(name).isDirty) {
      setValue(name, `#${color}`);
    }
  }, [color, getFieldState, methods, name, setValue]);

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <SVTextInput
        name={name}
        label={label}
        rules={colorPickerRules}
        inputProps={{
          endAdornment: (
            <InputAdornment
              data-testid="color-picker-icon"
              sx={{ cursor: 'pointer' }}
              onClick={() => setColorPickerOpen(true)}
              position="end">
              <Edit />
            </InputAdornment>
          ),
        }}
      />
      {colorPickerOpen && (
        <Box
          data-testid="color-picker-container"
          sx={{
            position: 'absolute',
            right: -215,
            bottom: '50%',
            zIndex: 1,
            border: '1px solid #000',
            borderRadius: 1,
            padding: '20px',
            background: '#fff',
          }}
          className="custom-pointer">
          <HexColorPicker color={color} onChange={handleOnChange} />
          <Button
            onClick={() => setColorPickerOpen(false)}
            sx={{ mt: '10px', width: '100%' }}
            variant="outlined">
            Confirm
          </Button>
        </Box>
      )}
    </Box>
  );
}
